import { theme, type ThemeConfig } from 'antd';

export type ThemeMode = 'light' | 'dark';

/** Chrome heights the layout needs in both CSS and JS, so they live in one place. */
/**
 * The sidebar's left rail. `gutter` is where every label, icon and the logo
 * begin; `railInset` is how far the hover/selected pill is held off the edge,
 * so the pill has air around it without the text moving.
 *
 * Shared with the counter app: the two sit side by side on the same desk often
 * enough that a different rail would read as a different product.
 */
export const NAV = { gutter: 16, railInset: 8 } as const;

/*
 * Wider sider than the counter app, and deliberately so.
 *
 * That app trades sider width for table width because its purchase grid is the
 * widest thing it draws. This one carries thirteen links across five sections —
 * "Manufacturers", "Delivery & fees", "Users & roles" — and truncating those to
 * win 40px would cost more in scanning than it gains in any one table. The
 * tables here scroll horizontally inside their own container instead.
 */
export const LAYOUT = {
  headerHeight: 52,
  siderWidth: 240,
  siderCollapsedWidth: 64,
  /*
   * Capping lower meant a 1920 monitor showed the same cramped grid as a laptop
   * while leaving 300px of empty page either side.
   */
  contentMaxWidth: 1440,
} as const;

/**
 * The logo, sampled. `public/logo.png` is the source of truth for these — if the
 * mark is ever redrawn, re-sample rather than nudge by eye.
 *
 * The cross is two greens over a pale leaf, and the wordmark is set in an
 * almost-black green rather than in black.
 *
 * The neutrals below borrow nothing from it. They are plain zinc — see the note
 * there for why tinting them toward the mark was a mistake.
 */
const BRAND = {
  /** The dark arm of the cross. The accent, and the colour of every primary action. */
  forest: '#16593f',
  /** Where the two arms overlap. */
  forestDeep: '#144934',
  /** The light arm. */
  sage: '#6eaf76',
  /** The leaf highlight. */
  mint: '#b6d9b8',
  /** The wordmark. Hue 161°, which is where the greys below get their cast. */
  ink: '#17342b',
} as const;

/**
 * Neutrals: zinc, the ramp shadcn/ui is built on. Untinted.
 *
 * An earlier version pulled these toward the wordmark's hue at 6-22% saturation
 * on the theory that a branded surface should carry the brand everywhere. It
 * did — every hover, every border and every muted label came out faintly green,
 * and a nav row lighting up on hover read as a coloured selection rather than as
 * a pointer. The whole point of the palette is that colour MEANS something, and
 * that only holds if the greys are grey.
 *
 * The green now appears exactly twice: a primary action, and the focus ring.
 */
const zinc = {
  50: '#fafafa',
  100: '#f4f4f5',
  200: '#e4e4e7',
  300: '#d4d4d8',
  400: '#a1a1aa',
  500: '#71717a',
  /* Half-step. Light-mode tertiary text needs it; see the note in PALETTE.light. */
  550: '#63636b',
  600: '#52525b',
  700: '#3f3f46',
  800: '#27272a',
  900: '#18181b',
  950: '#09090b',
} as const;

/**
 * The accent, straight off the logo.
 *
 * Light mode is the mark's own forest green, which carries white at 8.3:1 — the
 * brand colour and the accessible one turn out to be the same colour, which is
 * luck worth taking. On a near-black surface that same green is a smudge, so
 * dark mode lifts it to the point where a white label still clears AA (5.3:1)
 * and the filled shape still separates from the background.
 *
 * A green brand next to a green success tag is a real tension, and it is
 * resolved by weight and by placement: the accent is dark and desaturated and
 * appears only on a filled control, a link, or the focus ring, while `success`
 * below is brighter and only ever appears as a small tag. Nothing else in the
 * chrome is green at all — hovers and selections are grey. The signals
 * that actually demand attention here — expiring, expired, out of stock — are
 * amber and red, and neither is anywhere near either green.
 */
const ACCENT = { light: BRAND.forest, dark: '#1f7a55' } as const;

/**
 * Type, in three jobs.
 *
 * A stack, a fallback chain and a `tnum` figure set are the only things that
 * actually matter here; which faces fill the roles is a taste call, so both
 * candidate pairings are written out and `PAIRING` picks one. Changing that one
 * word swaps the whole product — every face below is already requested in
 * `index.html`.
 *
 *   display — page titles and large figures. Allowed to have a voice.
 *   body    — everything else: labels, tables, forms. Wants to disappear.
 *   mono    — money columns, SKUs, batch and order numbers. See `.tabular`.
 *
 * The mono role is the one worth keeping whichever pairing wins. A proportional
 * face with `tnum` lines figures up, but it cannot make a column of SKUs and
 * batch numbers scannable the way a true monospace does, and this product is
 * mostly columns of exactly that.
 */
const SYSTEM = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const PAIRINGS = {
  /** Parkinsans has the geometry and the wide apertures; Inter carries density. */
  parkinsans: {
    display: `'Parkinsans', ${SYSTEM}`,
    body: `'Inter', ${SYSTEM}`,
  },
  /** Drawn as a family, so the headings and the tables agree by construction. */
  geist: {
    display: `'Geist', ${SYSTEM}`,
    body: `'Geist', ${SYSTEM}`,
  },
} as const;

/** Swap to 'geist' to try the other pairing. Nothing else needs touching. */
const PAIRING: keyof typeof PAIRINGS = 'parkinsans';

export const FONT = {
  display: PAIRINGS[PAIRING].display,
  body: PAIRINGS[PAIRING].body,
  mono: "'Geist Mono', ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
} as const;

/**
 * Status colours, one step muted from the pure Tailwind hues. They appear on
 * tags, deltas and destructive buttons — never as decoration.
 */
const STATUS = {
  light: { success: '#16a34a', warning: '#d97706', error: '#dc2626', info: '#2563eb' },
  dark: { success: '#22c55e', warning: '#f59e0b', error: '#ef4444', info: '#60a5fa' },
} as const;

type Palette = {
  accent: string;
  bg: string;
  surface: string;
  elevated: string;
  /** Containers and dividers. Decorative, so it is allowed to be faint. */
  border: string;
  /**
   * The edge of anything you can type in or press.
   *
   * Separate and darker, because WCAG 1.4.11 asks 3:1 of the boundary that
   * identifies a control and the container hairline measures 1.24:1 against
   * white — which is why a row of input boxes read as ghosts. Cards and table
   * rules are exempt from that rule: losing sight of them costs nothing you
   * cannot infer from the layout.
   *
   * This reverses an earlier "one hairline for everything" decision. That was
   * right about consistency and wrong about legibility, and legibility wins on
   * a screen someone keys invoices into all day.
   */
  borderStrong: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  textQuaternary: string;
  fill: string;
  fillSecondary: string;
  fillTertiary: string;
  fillQuaternary: string;
  success: string;
  warning: string;
  error: string;
  /** Explanatory surfaces. Deliberately not the brand — see `colorInfo`. */
  info: string;
  /**
   * A destructive row's hover wash — Sign out in the account menu, today.
   *
   * antd derives nothing like it: its danger button is a solid fill, and a
   * solid red under a row you have merely pointed at reads as a thing already
   * happening. A tint at this weight says "this one is the red one" while the
   * label keeps doing the actual telling.
   */
  dangerSoft: string;
  /**
   * The same destructive row, hovered. A step deeper in light and a step
   * brighter in dark — hover intensifies, which is the only direction that
   * reads as "yes, this one".
   *
   * It also has to exist for a plainer reason. \`error\` clears AA on the panel
   * by a hair (4.83:1 light, 4.71:1 dark), so laying \`dangerSoft\` underneath it
   * drops the label to 4.27:1 and 3.98:1 — the wash breaks the very label it is
   * meant to emphasise. These two restore it to 5.7:1 and 5.4:1.
   */
  dangerStrong: string;
  /** Focus ring, three pixels of translucent accent — the shadcn focus tell. */
  ring: string;
  shadowSm: string;
  shadowMd: string;
};

const PALETTE: Record<ThemeMode, Palette> = {
  light: {
    accent: ACCENT.light,
    /*
     * The page is one step off white and panels are pure white. shadcn leans on
     * a border rather than a fill to separate a card, but a dense table screen
     * with no tonal step at all reads as one undifferentiated sheet, and this is
     * a screen someone scans rather than reads.
     */
    /*
     * Back to the lighter ground, now that it is safe.
     *
     * At this step the page and a white card differ by 1.04:1, which on its own
     * is under the threshold at which an edge registers — the reason it was
     * darkened before. What changed is that cards now carry a real hairline, so
     * the edge is drawn rather than inferred, and the lighter page reads calmer
     * behind a screen that is mostly table.
     */
    bg: zinc[50],
    surface: '#ffffff',
    elevated: '#ffffff',
    border: zinc[200],
    /* 3.4:1 on white, 3.1:1 on the page ground. Neutral, like the ramp. */
    borderStrong: '#8a8a91',

    /*
     * Text roles sit two steps darker in light mode than the ramp's midpoints
     * would suggest, and the reason is arithmetic rather than taste: against
     * white, zinc[500] measures 4.31:1 and zinc[400] 2.36:1, so the sidebar
     * labels, table headings and group headings were all below AA. Anything
     * that carries meaning is at or above 5:1 here; only `textQuaternary`,
     * which antd uses for disabled controls, is allowed to fall below it.
     */
    text: zinc[950],
    textSecondary: zinc[600],
    textTertiary: zinc[550],
    textQuaternary: zinc[400],
    fill: zinc[300],
    fillSecondary: zinc[200],
    fillTertiary: zinc[100],
    fillQuaternary: zinc[100],
    ...STATUS.light,
    dangerSoft: 'rgba(220, 38, 38, 0.08)',
    dangerStrong: '#b91c1c',
    ring: 'rgba(22, 89, 63, 0.22)',
    shadowSm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    shadowMd: '0 4px 12px -2px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
  },
  dark: {
    accent: ACCENT.dark,
    bg: zinc[950],
    surface: '#111113',
    elevated: zinc[900],
    border: zinc[800],
    /* 2.9:1 on the dark surface — the same job, from the other side. */
    borderStrong: '#5f5f66',
    text: zinc[50],
    textSecondary: zinc[400],
    textTertiary: zinc[500],
    textQuaternary: zinc[600],
    fill: zinc[700],
    fillSecondary: zinc[800],
    fillTertiary: '#1c1c1f',
    fillQuaternary: zinc[900],
    ...STATUS.dark,
    dangerSoft: 'rgba(239, 68, 68, 0.16)',
    dangerStrong: '#f87171',
    ring: 'rgba(31, 122, 85, 0.40)',
    shadowSm: '0 1px 2px 0 rgb(0 0 0 / 0.4)',
    shadowMd: '0 4px 12px -2px rgb(0 0 0 / 0.5), 0 2px 4px -2px rgb(0 0 0 / 0.4)',
  },
};

/**
 * Everything the Tailwind bridge in `theme-provider` republishes as CSS
 * variables, so a `bg-surface` div and an antd Card cannot drift apart. Exported
 * so there is exactly one place these values are written.
 */
export function palette(mode: ThemeMode): Palette {
  return PALETTE[mode];
}

/**
 * Seed tokens.
 *
 * `controlHeight: 36` is the default row height, with 30 and 44 either side.
 * This is a mouse and keyboard product worked in long sittings, so it stays
 * denser than a consumer app - but 32px with a 12px inset read as cramped
 * rather than efficient, and the padding is what carries that impression more
 * than the height does.
 *
 * The three heights are declared, not derived, because our Button and Input
 * hard-code the same three: a filter row has to line up without anyone
 * nudging a margin.
 */
function seedTokens(mode: ThemeMode): ThemeConfig['token'] {
  const p = PALETTE[mode];

  return {
    colorPrimary: p.accent,
    /*
     * Blue, not the brand.
     *
     * antd derives every informational surface from `colorInfo`, and pointing it
     * at the accent made each explanatory banner a green panel — which on a
     * screen whose only other green is the primary button reads as something to
     * press. An explanation is not an action, so it gets a colour that is not
     * used for one anywhere else in the product.
     */
    colorInfo: p.info,
    colorSuccess: p.success,
    colorWarning: p.warning,
    colorError: p.error,

    colorBgBase: p.bg,
    colorTextBase: p.text,

    colorBgLayout: p.bg,
    colorBgContainer: p.surface,
    colorBgElevated: p.elevated,
    colorBgSpotlight: mode === 'dark' ? zinc[800] : zinc[900],

    colorText: p.text,
    colorTextSecondary: p.textSecondary,
    colorTextTertiary: p.textTertiary,
    colorTextQuaternary: p.textQuaternary,

    /*
     * antd points both of these at `colorTextQuaternary` by default, which is
     * the disabled-control grey. A placeholder is instruction — "Type or click
     * to browse stock" is how someone learns what the field does — so it gets
     * the readable floor instead.
     */
    colorTextPlaceholder: p.textTertiary,
    colorTextDescription: p.textSecondary,

    /*
     * antd already splits these the way they need splitting: `colorBorder` is
     * the edge of a control, `colorBorderSecondary` the edge of a container.
     * They were collapsed to one value for visual consistency, which made every
     * input as faint as a divider. The control edge carries the contrast; the
     * container edge stays quiet.
     */
    colorBorder: p.borderStrong,
    colorBorderSecondary: p.border,

    colorFill: p.fill,
    colorFillSecondary: p.fillSecondary,
    colorFillTertiary: p.fillTertiary,
    colorFillQuaternary: p.fillQuaternary,

    // 8px outer, 6px on controls — `--radius` and `calc(--radius - 2px)`.
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    borderRadiusXS: 2,

    fontFamily: FONT.body,
    fontSize: 14,
    fontSizeLG: 15,
    fontSizeSM: 12,
    fontSizeHeading1: 28,
    fontSizeHeading2: 22,
    fontSizeHeading3: 18,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    lineHeight: 1.5,

    /*
     * 30 / 36 / 44, declared here so antd's Select, DatePicker and Segmented
     * land on the same three heights as our own Button and Input. Left to
     * itself antd derives SM and LG by multiplying (x0.75, x1.25), which from
     * 36 gives 27 and 45 — half-pixel rows next to a 30px button.
     */
    controlHeight: 36,
    controlHeightSM: 30,
    controlHeightLG: 44,

    /*
     * Nearly flat. shadcn separates surfaces with a border and reserves shadow
     * for things that genuinely float — a dropdown, a popover, a drawer.
     */
    boxShadow: p.shadowMd,
    boxShadowSecondary: p.shadowMd,
    boxShadowTertiary: p.shadowSm,

    /*
     * Short enough to feel like a response rather than an animation. Motion is
     * not switched off wholesale — antd's `motion: false` seed would also kill
     * the fade on drawers and dropdowns, where an instant appearance reads as a
     * glitch — but nothing in the chrome should take longer to settle than the
     * hand takes to move on.
     */
    motionDurationFast: '0.05s',
    motionDurationMid: '0.08s',
    motionDurationSlow: '0.12s',

    wireframe: false,
  };
}

/**
 * Component overrides. Each one exists to close a specific gap between antd's
 * defaults — tuned for a roomier, more colourful interface — and the compact,
 * near-monochrome one this app wants.
 */
function componentTokens(mode: ThemeMode): ThemeConfig['components'] {
  const p = PALETTE[mode];

  return {
    Layout: {
      bodyBg: p.bg,
      headerBg: p.surface,
      /*
       * The sider sits one step BACK from the content, not level with it. That
       * is what lets the selected item come forward as a raised card — on a
       * white sider a filled selection can only read as pressed, because there
       * is nothing lighter than the ground to lift it onto.
       */
      siderBg: p.bg,
      headerHeight: LAYOUT.headerHeight,
      headerPadding: '0 14px',
    },

    /*
     * The selected item is a neutral fill, not a tinted one. A coloured
     * selection in the sidebar competes with the accent on the primary button,
     * and in a screen full of status tags the only thing that should be teal is
     * the action you are about to take.
     */
    /*
     * The sidebar sits on ONE left rail at `NAV.gutter`, and three different
     * mechanisms have to agree on it:
     *
     *   group title  padding-inline, from the global `padding` token   → 16
     *   item content itemMarginInline + inlineIndent                   → 8 + 8
     *   the logo     the brand row's own padding in AppLayout          → 16
     *
     * `inlineIndent` is the one that bites. rc-menu writes it as an INLINE
     * `padding-left` (`useDirectionStyle`), so it beats `itemPaddingInline` and
     * no stylesheet can win against it — and because an item inside a group
     * counts as one level deep, antd's default of 24 pushed every icon 24px to
     * the right of its own group heading. It is set to 8 on the <Menu> itself.
     */
    Menu: {
      itemHeight: 36,
      itemBorderRadius: 6,
      itemMarginInline: NAV.railInset,
      itemMarginBlock: 2,
      itemPaddingInline: NAV.railInset,
      iconMarginInlineEnd: 10,
      iconSize: 16,
      fontSize: 14,
      groupTitleFontSize: 11,
      groupTitleColor: p.textSecondary,
      itemBg: 'transparent',
      subMenuItemBg: 'transparent',
      itemColor: p.textSecondary,
      itemHoverBg: p.fillTertiary,
      itemHoverColor: p.text,
      /*
       * The pressed state, and the one that was wrong.
       *
       * `itemActiveBg` defaults to `controlItemBgActive`, which antd derives
       * from the PRIMARY colour — so every click on a nav row flashed a
       * green-tinted block, the single loudest thing in the chrome, on a
       * sidebar whose whole design is that colour lives on the button and
       * nowhere else. A press is a neutral step darker than hover, no more.
       */
      itemActiveBg: p.fillSecondary,
      /*
       * Selected is the surface colour — lighter than the sider behind it — with
       * a hairline and a small shadow added in `styles.css`. Elevation carries
       * the state rather than colour, so the only saturated thing in the chrome
       * stays the primary button.
       *
       * The shadow lives in CSS because antd has no token for it, and it is a
       * box-shadow rather than a border so the item does not grow by 2px and
       * shift every row below it on selection.
       */
      itemSelectedBg: p.surface,
      itemSelectedColor: p.text,
      activeBarWidth: 0,
      collapsedWidth: LAYOUT.siderCollapsedWidth,

      /*
       * antd substitutes a whole parallel set of tokens when a Menu is themed
       * `dark`, and ignores every one of the light values above — which is why
       * the selected item rendered as a solid `colorPrimary` bar on a navy
       * `#001529` ground regardless of what was configured. Each one is
       * restated here against the same palette.
       */
      darkItemBg: 'transparent',
      darkSubMenuItemBg: 'transparent',
      darkPopupBg: p.elevated,
      darkItemColor: p.textSecondary,
      darkItemHoverBg: p.fillTertiary,
      darkItemHoverColor: p.text,
      darkItemSelectedBg: p.surface,
      darkItemSelectedColor: p.text,
      darkGroupTitleColor: p.textSecondary,
    },

    Button: {
      fontWeight: 500,
      contentFontSize: 14,
      contentFontSizeSM: 13,
      paddingInline: 14,
      paddingInlineSM: 10,
      paddingInlineLG: 18,
      // Flat. A shadow under a button is the clearest tell of a non-shadcn UI.
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      defaultBg: p.surface,
      defaultBorderColor: p.border,
      defaultColor: p.text,
      textHoverBg: p.fillTertiary,
    },

    Card: {
      bodyPadding: 16,
      headerPadding: 16,
      headerHeight: 46,
      headerFontSize: 15,
      headerBg: 'transparent',
      // A card is a bordered surface, never a floating one.
      boxShadowTertiary: 'none',
    },

    /*
     * Header is a plain row with a rule under it — no fill, no rounded corners,
     * no vertical splitters. Rows are 34px tall against antd's 46.
     */
    /*
     * Compact by default, because these screens are read as columns.
     *
     * Row height is the whole argument: at 10px of block padding a laptop shows
     * about twelve rows of a list someone is scanning for one that differs, and
     * scrolling to compare two things that could have been on screen together is
     * the cost. At 7px it shows eighteen, and nothing became harder to hit — the
     * row is still 34px, which clears the 24px target guidance comfortably.
     *
     * The header is transparent and unsplit: a filled grey band across the top
     * of a table draws a horizontal line the eye keeps returning to, when the
     * thing worth following is the column going down.
     */
    Table: {
      headerBg: 'transparent',
      headerColor: p.textTertiary,
      headerSplitColor: 'transparent',
      headerBorderRadius: 0,
      borderColor: p.border,
      cellPaddingBlock: 7,
      cellPaddingInline: 12,
      cellPaddingBlockSM: 5,
      cellPaddingInlineSM: 10,
      cellFontSize: 13.5,
      cellFontSizeSM: 12.5,
      rowHoverBg: p.fillTertiary,
      footerBg: 'transparent',
      stickyScrollBarBg: p.fill,
    },

    Input: {
      paddingInline: 10,
      paddingBlock: 4,
      activeBorderColor: p.accent,
      hoverBorderColor: mode === 'dark' ? zinc[600] : zinc[500],
      activeShadow: `0 0 0 3px ${p.ring}`,
      errorActiveShadow: `0 0 0 3px ${p.error}33`,
      addonBg: p.fillTertiary,
    },

    InputNumber: {
      paddingInline: 10,
      activeBorderColor: p.accent,
      hoverBorderColor: mode === 'dark' ? zinc[600] : zinc[500],
      activeShadow: `0 0 0 3px ${p.ring}`,
    },

    /*
     * Both option backgrounds are stated rather than inherited. antd derives
     * `optionSelectedBg` from `controlItemBgActive` and `optionActiveBg` from
     * `controlItemBgHover` — the same primary-tinted aliases that turned the
     * sidebar's pressed state into a green block. In a dropdown of medicine
     * names those tints are worse than merely loud: the row under the cursor is
     * the one being read, and a saturated fill behind two lines of small grey
     * text is where a wrong strength gets picked.
     *
     * Hover is the lighter of the two and selection the heavier, so a list you
     * are moving through never looks like a list with something chosen in it.
     */
    Select: {
      optionActiveBg: p.fillTertiary,
      optionSelectedBg: p.fillSecondary,
      optionSelectedColor: p.text,
      optionSelectedFontWeight: 600,
      optionPadding: '5px 10px',
      activeBorderColor: p.accent,
      hoverBorderColor: mode === 'dark' ? zinc[600] : zinc[500],
      activeOutlineColor: p.ring,
      selectorBg: p.surface,
      /* The clear cross is an action, so it darkens on approach. */
      clearBg: p.surface,
      multipleItemBg: p.fillTertiary,
    },

    DatePicker: {
      activeBorderColor: p.accent,
      hoverBorderColor: mode === 'dark' ? zinc[600] : zinc[500],
      activeShadow: `0 0 0 3px ${p.ring}`,
      cellActiveWithRangeBg: p.fillSecondary,
    },

    Form: {
      itemMarginBottom: 16,
      verticalLabelPadding: '0 0 5px',
      labelFontSize: 13,
      labelColor: p.text,
      labelRequiredMarkColor: p.error,
    },

    /* A tag is a shadcn badge: small, quiet, and never the loudest thing in a row. */
    Tag: {
      defaultBg: p.fillTertiary,
      defaultColor: p.textSecondary,
      borderRadiusSM: 5,
      fontSizeSM: 12,
      lineHeightSM: 1.5,
    },

    Alert: {
      defaultPadding: '9px 12px',
      withDescriptionPadding: '12px 16px',
      withDescriptionIconSize: 18,
    },

    Segmented: {
      trackBg: p.fillTertiary,
      trackPadding: 2,
      itemSelectedBg: p.surface,
      itemSelectedColor: p.text,
      itemColor: p.textSecondary,
      borderRadius: 6,
      borderRadiusSM: 5,
    },

    Modal: { titleFontSize: 16, headerBg: p.elevated, contentBg: p.elevated, paddingContentHorizontalLG: 20 },

    Drawer: { paddingLG: 20, footerPaddingBlock: 12, footerPaddingInline: 20 },

    Dropdown: { paddingBlock: 4, controlItemBgHover: p.fillTertiary, borderRadiusLG: 8 },

    Tooltip: { borderRadius: 6, controlHeight: 28 },

    Descriptions: { itemPaddingBottom: 12, labelBg: 'transparent', labelColor: p.textSecondary },

    Statistic: { contentFontSize: 24, titleFontSize: 12 },

    Popover: { borderRadiusLG: 8, titleMinWidth: 160 },

    Skeleton: { gradientFromColor: p.fillTertiary, gradientToColor: p.fillSecondary },

    Switch: { trackMinWidth: 38, trackHeight: 20, handleSize: 16 },

    Divider: { marginLG: 16 },

    Empty: { controlHeightLG: 40 },

    Result: { titleFontSize: 18, subtitleFontSize: 14 },

    Avatar: { borderRadius: 6 },

    Spin: { dotSize: 18 },
  };
}

export function themeConfig(mode: ThemeMode): ThemeConfig {
  return {
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: seedTokens(mode),
    components: componentTokens(mode),
    /*
     * CSS variables mean a mode switch re-paints from the same stylesheet
     * rather than regenerating every component's styles — and it lets the
     * Tailwind bridge in ThemeProvider read live values.
     */
    cssVar: { key: 'tlp' },
    hashed: true,
  };
}
